import { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { GoBack, LayoutContext, Tabs } from '@/_shared/components';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { useTabs } from '@/_shared/hooks/useTabs';
import { TabAssessmentTeam, TabCreditsTeam, TabDepartmentTeam } from '../components';
import { getDepartmentTeamsCall } from '../state';

export const DepartmentsTeamsDetailScreen = () => {
  const t = useTranslations('departments.details.tabs');
  const { isFeatureEnabled } = useFeatureFlags();
  const creditsFF = isFeatureEnabled(FeatureFlag.CREDITS);

  const { setLayout } = useContext(LayoutContext);

  const { isDesktop } = useBreakingPoints();

  const { departmentId, teamId } = useParams();

  const {
    execute: executeGetDepartmentTeams,
    loading: loadingGetDepartmentTeams,
    success: successGetDepartmentTeams,
    error: errorGetDepartmentTeams,
    response: responseGetDepartmentTeams,
  } = getDepartmentTeamsCall.hook();

  const teamName = useMemo(() => {
    return responseGetDepartmentTeams?.data.find(team => team.id === teamId)?.name || '';
  }, [responseGetDepartmentTeams, teamId]);

  const tabsInitialState = creditsFF
    ? [
        {
          title: t('team'),
          index: 0,
          active: true,
          dataTestId: 'team',
        },
        {
          title: t('tab2'),
          index: 1,
          active: false,
          dataTestId: 'assessment',
        },
        {
          title: t('tab3'),
          index: 2,
          active: false,
          dataTestId: 'credits',
        },
      ]
    : [
        {
          title: t('team'),
          index: 0,
          active: true,
          dataTestId: 'team',
        },
        {
          title: t('tab2'),
          index: 1,
          active: false,
          dataTestId: 'assessment',
        },
      ];

  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  useEffect(() => {
    if (departmentId) {
      executeGetDepartmentTeams({ departmentId });
    }
  }, [departmentId, executeGetDepartmentTeams]);

  useEffect(() => {
    setLayout({ title: 'departments' });
  }, [setLayout, t]);

  return (
    <Box>
      <GoBack />
      <Box>
        <Box overflowX={isDesktop ? 'auto' : 'scroll'}>
          <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
        </Box>
        {tabs.map(tab => {
          return (
            <Box key={tab.title} display='flex' flexDirection='column'>
              {tab.index === 0 && tab.active && (
                <TabDepartmentTeam
                  departmentId={departmentId}
                  teamId={teamId}
                  teamName={teamName}
                  loading={loadingGetDepartmentTeams}
                  error={errorGetDepartmentTeams}
                  success={successGetDepartmentTeams}
                />
              )}
              {tab.index === 1 && tab.active && (
                <TabAssessmentTeam
                  departmentId={departmentId}
                  teamId={teamId}
                  teamName={teamName}
                />
              )}
              {tab.index === 2 && tab.active && (
                <TabCreditsTeam departmentId={departmentId} teamId={teamId} teamName={teamName} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
